import './LabelDesignerTemplateCreatorModal.css';
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../../components/controls/inputs";
import BaseCreator from "../../../components/creators/baseCreator";
import { useForm } from "../../../system/hooks/useForm";
import Designer from "../../../components/Designer/Designer";
import { ILabelDesignerProperty } from "../../Reports/Shared/Interfaces/ILabelDesignerProperty";
import { SelectProps } from "antd";
import { v4 as uuidv4 } from 'uuid';
import { useAppContext } from "../../../system/providers/appContextProvider";
import { LabelPrintingDataProvider } from "../../../Services/DataProviders/LabelPrintingDataProvider";
import { makeOptions } from "../../../components/Designer/Consts";
import { ILabelDesignerTemplateDTO } from "../../../libs/coreapi-dto/dirs/labelDesignerTemplate";
import { isNullOrWhiteSpace } from "../../Reports/Common/CommonFunctions";
import _ from 'lodash';
import GridWrapper from '../../../components/controls/GridWrapper';
import GridSelectorInput from '../../../components/controls/GridSelectorInput';
import { ContractorSelectorModal } from '../../Dictionaries/Contractor';
import { IEntitySimpleDTO } from '../../../libs/coreapi-dto/@types/common';
import { Spinner } from '../../../components/spiner/Spinner';
import { useUserContext } from '../../../system/providers/userContextProvider';

interface IValidator {
    name: string,
    contractor: IEntitySimpleDTO
}

export const LabelDesignerTemplateCreatorModal: FC<ICreatorModalProps<ILabelDesignerTemplateDTO>> = (props) => {
    const [entity, setEntity] = useState<ILabelDesignerTemplateDTO>(props.data ?? {} as ILabelDesignerTemplateDTO);
    const [contractor, setContractor] = useState(props.data?.contractor as IEntitySimpleDTO);

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.labelDesignerTemplate.' + value)
    const errorsT = (value: string) => t('errors.' + value);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [uid] = useState<string>(uuidv4())
    const [elements, setElements] = useState<Array<ILabelDesignerProperty>>([]);
    const [options, setOptions] = useState<SelectProps['options']>([]);
    const appCtx = useAppContext()
    const dataProvider = new LabelPrintingDataProvider(appCtx.reportAPIService)
    const refDesigner = useRef<any>(null);
    const [isStuffLoading, setIsStuffLoading] = useState<boolean>(true);
    const userContext = useUserContext();
    const isSuperUser = userContext.userRoles.includes('SuperUser');
    const isAdministrator = userContext.userRoles.includes('Administrator');

    useEffect(() => {
        // componentDidMount events
        dataProvider.getDesignerElements((e: Array<ILabelDesignerProperty>) => {
            const totalCount = e.length
            if (totalCount > 0) {
                setElements(e)
                const options = makeOptions(e)
                setOptions(options)
            }
            setIsStuffLoading(false)
        }, () => {
            setIsStuffLoading(false)
        })

        return () => {
            // componentWillUnmount events
        }
    }, [])

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            name: {
                required: {
                    value: entity.name ? false : true,
                    message: errorsT("required")
                }
            },
            contractor: {
                required: {
                    value: contractor ? false : true,
                    message: errorsT("required")
                }
            }
        }
    });

    const handleSubmit = () => {
        handleSaveClick()
        const canSave = isValid()
        if (canSave) {
            props.save({ ...entity, contractor: contractor })
            setIsSubmitting(true)
        }
    }

    const handleSaveClickEx = async () => {
        handleSubmit()
    }

    async function handleModelChange(xml: string) {
        // try {
        //   const response = await axios.post(urlPreview, { Item1: xml, Item2: (props.plugin.record as LabelTemplate).uid });
        //   if (response) {
        //     setLastChangesPdf(response.data)
        //   }
        // } catch (error: any) {
        //   setError(error);
        // }
    }

    function handleSaveClick(): boolean {
        const result = refDesigner.current.save()
        const json = result.json

        let nodesChanged: boolean
        if (!isNullOrWhiteSpace(entity.template)) {
            let templateModified = JSON.parse(json);
            let templateSaved = JSON.parse(entity.template as string);
            nodesChanged = !_.isEqual(templateModified, templateSaved)
        } else {
            nodesChanged = true
        }
        const modified: boolean = nodesChanged
        if (modified) {
            entity.template = json
        }
        return modified
    }

    function onExport(): string {
        return entity.name
    }

    return <>
        <BaseCreator
            variant={props.variant}
            isSubmitting={isSubmitting}
            save={handleSubmit}
            cancel={props.cancel}
            valid={!props.lockFromPermission}
        >
            {
                isStuffLoading ? <Spinner /> //Нельзя дизайнер отображать, пока не загрузились элементы дизайнера, а то не поспеет
                    :
                    <>
                        <GridWrapper cols={2}>
                            <TextInput
                                labelVariant="required"
                                required
                                label={baseT('name')}
                                value={entity.name} error={errors.name}
                                onChange={(value) => setEntity({ ...entity, name: value })}
                                onFocus={() => setErrors({ ...errors, name: undefined })}
                                disabled={props.lockFromPermission}
                            />
                            <GridSelectorInput
                                required
                                disabledInput={true}
                                id='idContractorGlobal'
                                selectorModalJsx={ContractorSelectorModal}
                                label={baseT('contractor')}
                                selectedEntity={contractor}
                                onSelect={(entity1) => setContractor(entity1)}
                                error={errors.contractor} onFocus={() => setErrors({ ...errors, contractor: undefined })}
                                disabled={props.lockFromPermission}
                                applyUserFilter={!(isAdministrator || isSuperUser)}
                            />
                        </GridWrapper>
                        <Designer
                            options={elements}
                            selectProps={options}
                            uid={uid}
                            template={props.data?.template as string}
                            ref={refDesigner}
                            onSave={handleSaveClickEx}
                            onModelChange={handleModelChange}
                            showBorderOptions={true}
                            disabled={props.lockFromPermission}
                            onExport={onExport}
                        />
                    </>
            }
        </BaseCreator>
    </>
}